/* eslint-disable new-cap */
import { gql } from "@apollo/client";
import { Instance as instance } from "../fragments";
import SEARCH_SYNOPSIS_TERMS from "../fragments/SearchSynopsisTerms";

const FETCH_INSTANCE = props => gql`
  query FetchInstance($id: Int) {
    instance(id: $id) {
      ${instance(props)}
    }
  }
`;

const FETCH_INSTANCES = props => gql`
  query FetchInstances($input: InstanceFilterInput!) {
    instances(input: $input) {
      count
      edges {
        ${instance(props)}
      }
      error
    }
  }
`;

const FETCH_INSTANCE_DESCRIPTIONS = props => gql`
  query FetchInstanceDescriptions($input: AdvInstanceFilterInput!) {
    instancesV2(input: $input) {
      count
      edges {
        id
        description
      }
    }
  }
`;

const FETCH_INSTANCE_SALES_DESCRIPTIONS = props => gql`
  query FetchInstanceSalesDescriptions($input: AdvInstanceFilterInput!) {
    instancesV2(input: $input) {
      count
      edges {
        id
        description
        value {
          value
          currency {
            code
          }
        }
      }
    }
  }
`;

const FETCH_INSTANCES_V2 = props => gql`
  query FetchInstancesV2($input: AdvInstanceFilterInput!) {
    instancesV2(input: $input) {
      count
      edges {
        ${instance(props)}
      }
      sqlQuery
    }
  }
`;

// Minimal lookup used to resolve a human reference (e.g. "JJ71178") to the
// numeric instance id so URLs like /instance/JJ71178 can redirect.
const FETCH_INSTANCE_ID_BY_REFERENCE = gql`
  query FetchInstanceIdByReference($input: AdvInstanceFilterInput!) {
    instancesV2(input: $input) {
      count
      edges {
        id
        name
      }
    }
  }
`;

const INSTANCE_LIST_FIELDS = `
  count
  edges {
    id
    edition {
      artwork {
        id
        dimensions
        medium
        artists {
          name
          copyright
        }
        imagesSummary {
          imgT
          imgX
        }
        title
        images {
          photoMaterial {
            photographer
            credit
          }
        }
        editions {
          year
        }
      }
      number
      year
    }
    editionNumberFormatted
    name
    status {
      name
    }
    currentWarehouseMove {
      siteId
      shipmentId
      shipment {
        id
        date
        dispatched
        delivered
      }
      site {
        name
      }
      location {
        description
        viewingRoom {
          description
        }
      }
    }
    value {
      value
      currency {
        code
      }
    }
  }
  sqlQuery
`;

const FETCH_INSTANCES_LIST = gql`
  query FetchInstancesV2($input: AdvInstanceFilterInput!) {
    instancesV2(input: $input) {
      ${INSTANCE_LIST_FIELDS}
    }
  }
`;

// The results page also shows the search as removable tags.
const FETCH_INSTANCES_SEARCH_RESULTS = gql`
  query FetchInstancesSearchResults($input: AdvInstanceFilterInput!) {
    instancesV2(input: $input) {
      ${INSTANCE_LIST_FIELDS}
      ${SEARCH_SYNOPSIS_TERMS}
    }
  }
`;

// Lean per-instance summary for the brochure wizard: enough to render the
// ordering list (name, artist, title, thumbnail) and the per-instance image
// slot carousels (the artwork's full image set) in a single query.
const FETCH_BROCHURE_WIZARD_INSTANCES = gql`
  query FetchBrochureWizardInstances($input: AdvInstanceFilterInput!) {
    instancesV2(input: $input) {
      count
      edges {
        id
        name
        editionNumberFormatted
        edition {
          id
          number
          year
          artwork {
            id
            title
            series
            precis
            artists {
              id
              name
              publicCollections
              # The biography page's editable text starts as whatever the
              # artist's contact record says.
              contact {
                biography
              }
            }
            images {
              archiveId
              order
              imgT
              imgX
            }
          }
        }
      }
    }
  }
`;

const FETCH_INSTANCE_GALLERY = gql`
  query FetchInstanceGallery($id: Int) {
    instance(id: $id) {
      id
      name
      description
      editionNumberFormatted
      edition {
        id
        number
        year
        artwork {
          id
          title
          description
          medium
          dimensions
          precis
          artists {
            id
            name
            publicCollections
            contact {
              surname
            }
          }
          images {
            archiveId
            order
            imgT
            imgX
          }
        }
      }
      status {
        name
      }
      value {
        value
        date
        currency {
          code
        }
      }
      consignmentValue {
        value
        date
        currency {
          code
        }
      }
    }
  }
`;

export {
  FETCH_INSTANCE,
  FETCH_BROCHURE_WIZARD_INSTANCES,
  FETCH_INSTANCE_GALLERY,
  FETCH_INSTANCES,
  FETCH_INSTANCES_V2,
  FETCH_INSTANCES_LIST,
  FETCH_INSTANCES_SEARCH_RESULTS,
  FETCH_INSTANCE_DESCRIPTIONS,
  FETCH_INSTANCE_SALES_DESCRIPTIONS,
  FETCH_INSTANCE_ID_BY_REFERENCE,
};
